<script src="../../../api/Interceptors.js"></script>
<script src="../../../api/IframeService.js"></script>
<template>
  <div id="appointments-page">

    <Breadcrumbs
      :breadcrumbs="[
        { title: this.$t(`menu.${this.serviceSlug}`)},
        { title: this.$t('clinical.appointments.title'), disabled: true }
      ]">

      <div slot="breadcrumbAfter" class="d-flex align-items-center">
        <va-popover
          v-if="canBulkUpdateAppointments"
          class="mr-3"
          placement="left"
          :message="$t('clinical.appointments.bulk_transfer_appointments')"
        >
          <span class="btn__round">
            <va-button
              id="btn-bulk-update-appointments"
              @click.prevent="showBulkUpdateAppointmentsModal = !showBulkUpdateAppointmentsModal">
              <i class="fa fa-refresh"></i>
            </va-button>
          </span>
        </va-popover>

        <va-popover
          v-if="pendingActivitiesWritePermissions.length"
          class="mr-3"
          placement="left"
          :message="$t('entities.activities.new_pending_activity')"
        >
          <span class="btn__round">
            <va-button
              id="btn-add-activity"
              @click.prevent="showNewPendingActivity = true">
              <i class="fa fa-plus"></i>
            </va-button>
          </span>
        </va-popover>

        <va-popover
          v-if="canSyncSPICE"
          placement="left"
          :message="$t('clinical.appointments.import')"
        >
          <span class="fa fa-upload" @click="syncSPICE"></span>
        </va-popover>
      </div>

    </Breadcrumbs>

    <fade-transition
      :duration="400"
      mode="out-in">
      <div class="btn btn__floating btn__round__large btn__round__large"
           v-if="canCreateAppointment && canCreateForDefaults()">
        <va-popover
          :message="$t('clinical.appointments.create')"
          placement="left"
        >
          <va-button
            @click.prevent="createWithDefaults">
            <i class="fa fa-plus"></i>
          </va-button>

        </va-popover>

      </div>
    </fade-transition>

    <div class="evollu-form">
      <div class="form-row">
        <div class="col-12 col-md-4">
          <div>
            <label class="form-label">{{$t('clinical.appointments.facility')}}:</label>
            <multiselect
              v-model="filters.facilities"
              :options="facilitiesOptions"
              track-by="id"
              label="name"
              select-label=""
              selected-label=""
              deselect-label=""
              placeholder="---"
              :allow-empty="true"
              :multiple="true"
              :searchable="true"
              :disabled="readEntityActivityUsers.length === 1"
              @input="updateOptionsBasedOnFacilities">
              <template slot="clear">
                <span class="multiselect__clear" v-if="filters.facilities.length"
                      @mousedown.prevent.stop="clearFacilitiesMultiselect"></span>
              </template>
            </multiselect>
          </div>
        </div>

        <div class="col-12 col-md-4">
          <div>
            <label class="form-label">{{$t('clinical.appointments.dispenser')}}:</label>
            <multiselect
              v-model="filters.users"
              :options="usersOptions"
              track-by="id"
              :custom-label="createDispenserNameLabel"
              select-label=""
              selected-label=""
              deselect-label=""
              placeholder="---"
              :allow-empty="true"
              :multiple="true"
              :searchable="true"
              :disabled="readEntityActivityUsers.length === 1"
              @input="updateOptionsBasedOnUsers"
            >
              <template slot="clear">
                <span class="multiselect__clear" v-if="filters.users.length"
                      @mousedown.prevent.stop="clearUsersMultiselect"></span>
              </template>
            </multiselect>
          </div>
        </div>

        <div class="col-12 col-md-4">
          <div>
            <label class="form-label">{{$t('clinical.appointments.activity')}}:</label>
            <multiselect
              v-model="filters.activities"
              :options="activitiesOptions"
              track-by="id"
              label="name"
              :customLabel="option => $t(`clinical.appointments.activity_types.${option.id}`, option.name)"
              select-label=""
              selected-label=""
              deselect-label=""
              placeholder="---"
              :allow-empty="true"
              :multiple="true"
              :searchable="true"
              :disabled="(!filters.facilities.length && !filters.users.length) || activitiesOptions.length === 1 "
              @input="updateOptionsBasedOnActivities"
            >
              <template slot="option" slot-scope="props">
                {{ $t(`clinical.appointments.activity_types.${props.option.id}`, props.option.name) }}
              </template>
              <template slot="clear">
                <span class="multiselect__clear" v-if="filters.activities.length"
                      @mousedown.prevent.stop="clearActivitiesMultiselect"></span>
              </template>
            </multiselect>
          </div>
        </div>

      </div>
    </div>

    <div class="calendar-wrapper">

      <loading
        :active.sync="isLoading"
        :can-cancel="false"
        :color="'#29c4a9'"
        :background-color="'#f5f5f5'"
        :is-full-page="false">
      </loading>

      <FullCalendar
        id="appointmentAgenda"
        :key="calendarKey"
        ref="fullCalendar"
        :options="calendarOptions">
        <template v-slot:eventContent='data'>
          <Appointment
            :view="data.view"
            :event="data.event"
          />
        </template>
      </FullCalendar>
    </div>

    <AppointmentDetails
      v-if="showAppointmentInfo"
      v-bind="propsAppointmentForm"
      :service="component.service"
      :inactiveEntityActivityUsers="inactiveEntityActivityUsers"
      @modal-closed="modalClosed"
      @appointment-updated="appointmentUpdated"
      @appointment-deleted="updateCalendar"
      @appointment-reopened="updateCalendar"/>

    <AppointmentForm
      v-if="showAppointmentForm"
      v-bind="propsAppointmentForm"
      :service="component.service"
      @modal-closed="modalClosed"
      @appointment-created="updateCalendar"/>

    <EditUserActivityDateModal
      v-if="showNewPendingActivity"
      :user="$auth.user()"
      :pending="true"
      @created="onEntityActivityUserCreated"
      @modalClosed="onCloseEditUserActivityDate"/>

    <BulkUpdateAppointmentsModal
      v-if="showBulkUpdateAppointmentsModal"
      :selected-facilities="filters.facilities"
      :selected-users="filters.users"
      :selected-activities="filters.activities"
      :appointments="appointments"
      :startDate="lowestAppointmentStart"
      :endDate="highestAppointmentEnd"
      :component="component"
      @modal-cancel="onCancelBulkUpdateAppointmentsModal"
      @appointments-updated="bulkUpdatedAppointmentsActivity"
      />

    <ConfirmationModal
      v-if="newPendingActivityCreated"
      :title="$t('entities.activities.new_pending_activity')"
      :body="null"
      :noOutsideDismiss="false"
      :showOkay="false"
      :cancelText="$t('modals.buttons.back')"
      @modal-canceled="newPendingActivityCreated = false">

      <div slot="body" class="d-flex justify-content-center align-items-center flex-column">
        <i class="i-email-sent w-100 h-100" style="padding: 5.5rem;"></i>
        <div class="mx-5 mt-5 mb-5 font-weight-bold d-flex flex-column justify-content-center align-items-center">
          <span> {{ $t('entities.activities.pending.created') }}</span>
          <span> {{ $t('entities.activities.pending.email_sent')}}</span>
        </div>
      </div>
    </ConfirmationModal>

  </div>
</template>
<script>
import axios from 'axios'
import moment from 'moment'
import Appointment from './Appointment'
import ApiService from '@/api/ApiService'
import Errors from '@/utils/StandardErrors'
import Multiselect from 'vue-multiselect'
import Loading from 'vue-loading-overlay'
import AppointmentForm from './AppointmentForm'
import FullCalendar from '@fullcalendar/vue'
import momentPlugin from '@fullcalendar/moment'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import AppointmentDetails from './AppointmentDetails'
import interactionPlugin from '@fullcalendar/interaction'
import Breadcrumbs from '@/components/common/Breadcrumbs'
import { AppointmentService } from '@/api/AuthClinicalService'
import LocalStorageHelper from '@/local-storage/LocalStorageHelper'
import ConfirmationModal from '@/components/common/ConfirmationModal'
import FadeTransition from '@/components/common/transitions/FadeTransition'
import EditUserActivityDateModal from '@/components/users/components/Activities/EditUserActivityDateModal'
import { FILTERS_CALENDAR_DAY, FILTERS_CALENDAR_VIEW, FILTERS_CALENDAR } from '@/local-storage/LocalStorageKeys'
import { mapGetters } from 'vuex'
import { uniqBy, flatten } from 'lodash'
import { FORM_DEFAULTS } from './config'
import BulkUpdateAppointmentsModal from '@/components/clinical/appointments/BulkUpdateAppointmentsModal'

const AppointmentInfoType = 200
const PendingActivitiesInfoType = 141
const AppointmentDetailsInfoType = 300
const accessTypeRead = 1
const accessTypeWrite = 2
const DispenserFieldId = 8
const DispenserPremises = 9

const calendarMinTime = '08:00:00'
const calendarMaxTime = '20:00:00'

const EntityActivityUserActiveStatus = 1
const EntityActivityUserInactiveStatus = 3

export default {
  props: {
    serviceSlug: {
      type: String,
      required: true
    },
    component: {
      type: Object,
      required: true
    }
  },
  components: {
    BulkUpdateAppointmentsModal,
    Loading,
    Appointment,
    Breadcrumbs,
    Multiselect,
    FullCalendar,
    FadeTransition,
    AppointmentDetails,
    AppointmentForm,
    ConfirmationModal,
    EditUserActivityDateModal
  },
  data: function () {
    return {
      appointments: [],
      showBulkUpdateAppointmentsModal: false,
      isLoading: true,
      hasLoadedLocalStorage: false,
      showNewPendingActivity: false,
      newPendingActivityCreated: false,
      cancelationReadCallback: null,
      cancelationWriteCallback: null,
      cancelationAppointmentsCallback: null,
      calendarKey: 0,
      filters: {
        facilities: [],
        activities: [],
        users: []
      },
      propsAppointmentForm: {
        facilities: [],
        entityActivityUsers: [],
        appointment: null,
        fromEvent: {
          start: null,
          end: null
        },
      },
      showAppointmentForm: false,
      showAppointmentInfo: false,
      calendarOptions: {
        eventSources: [{
          events: (options, callback) => {
            if (this.hasLoadedLocalStorage) {
              if (this.cancelationAppointmentsCallback !== null) {
                this.cancelationAppointmentsCallback()
              }

              let params = {
                startDate: moment(options.start).format('YYYY-MM-DD'),
                facilities: this.filters.facilities.map(facility => facility.id).join(','),
                activities: this.filters.activities.map(activity => activity.id).join(','),
                users: this.filters.users.map(user => user.id).join(','),
                no_parent: true
              }

              if (moment(options.end).format('HH:mm:ss') === '00:00:00') {
                params.endDate = moment(options.end).subtract(1, 'second').format('YYYY-MM-DD')
              }

              this.isLoading = true
              ApiService.cancelableQuery(`${this.component.service.url}/admin/appointments`, params, new axios.CancelToken(c => {
                this.cancelationAppointmentsCallback = c
              }))
                .then(response => {
                  this.appointments = response.data.data.map(appointment => ({
                    ...appointment,
                    start: this.$options.filters.localTime(appointment.start),
                    end: this.$options.filters.localTime(appointment.end),
                    started_at: appointment.started_at ? this.$options.filters.localTime(appointment.started_at) : null,
                    ended_at: appointment.ended_at ? this.$options.filters.localTime(appointment.ended_at) : null
                  }))

                  callback(this.appointments.map(appointment => ({
                    ...appointment,
                    borderColor: appointment.type && appointment.type.color ? appointment.type.color : '#29c4a9',
                  })))
                })
                .catch(errors => {
                  if (!axios.isCancel(errors)) {
                    console.log(errors)
                  }
                })
                .finally(() => {
                  this.cancelationReadCallback = null
                  this.isLoading = false
                })
            }
          }
        }],
        lazyFetching: false,
        eventColor: '#29c4a9',
        plugins: [dayGridPlugin, timeGridPlugin, momentPlugin, interactionPlugin],
        dayMaxEventRows: 3,
        headerToolbar: {
          left: 'title',
          right: 'timeGridDay timeGridWeek dayGridMonth prev next'
        },
        eventDisplay: 'block',
        buttonText: {
          today: this.$t('agenda.buttons.today'),
          month: this.$t('agenda.buttons.month'),
          week: this.$t('agenda.buttons.week'),
          day: this.$t('agenda.buttons.day'),
          listMonth: this.$t('agenda.buttons.listMonth'),
        },
        customButtons: {
          timeGridDay: {
            text: this.$t('agenda.buttons.day'),
            click: (event) => {
              this.calendarAPI.gotoDate(this.getNextViewStart())
              this.calendarAPI.changeView('timeGridDay')
            }
          },
          timeGridWeek: {
            text: this.$t('agenda.buttons.week'),
            click: () => {
              this.calendarAPI.gotoDate(this.getNextViewStart())
              this.calendarAPI.changeView('timeGridWeek')
            }
          }
        },
        initialDate: LocalStorageHelper.get(FILTERS_CALENDAR_DAY, false),
        initialView: LocalStorageHelper.get(FILTERS_CALENDAR_VIEW) || 'dayGridMonth',
        locale: this.selectedLanguage,
        firstDay: 1,
        hiddenDays: [0],
        allDayContent: this.$t('agenda.buttons.all-day'),
        allDayClassNames: 'calendar__allday__label',
        slotMinTime: calendarMinTime,
        slotMaxTime: calendarMaxTime,
        titleFormat: 'MMMM, YYYY',
        displayEventEnd: true,
        nowIndicator: true,
        slotDuration: '00:30',
        slotLabelFormat: 'HH:mm',
        slotLabelInterval: '01:00',
        height: 'auto',
        dayHeaderFormat: 'ddd D',
        eventTimeFormat: 'H:mm',
        editable: true,
        weekends: true,
        allDaySlot: false,
        selectable: true,
        selectMirror: true,
        unselectAuto: true,
        navLinks: true,
        dayHeaderContent: (content) => {
          return {
            html: `
          <div class="calendar__header__label">
            <div class="calendar__header__label__day view__${content.view.type}">` + moment(content.date).locale(this.selectedLanguage === 'gb' ? 'en' : this.selectedLanguage).format('DD') + `</div>
            <div class="calendar__header__label__weekday">` + moment(content.date).locale(this.selectedLanguage === 'gb' ? 'en' : this.selectedLanguage).format('dddd') + `</div>
          </div>
        `
          }
        },
        eventClick: this.eventSelected,
        eventDrop: this.updateEventFromUI,
        // eventResize: this.updateEventFromUI,
        select: this.eventCreated,
        navLinkDayClick: this.dayClicked,
        datesSet: this.datesDidUpdate
      },
      calendarAPI: null,
      readEntityActivityUsers: [],
      writeEntityActivityUsers: [],
      inactiveEntityActivityUsers: [],
      facilitiesOptions: [],
      usersOptions: [],
      activitiesOptions: [],
      completeFacilities: [],
      completeUsers: [],
      completeActivities: []
    }
  },
  mounted: function () {
    this.calendarAPI = this.$refs.fullCalendar.getApi()
    if (this.calendarAPI) {
      this.calendarAPI.setOption('locale', this.selectedLanguage)
    }
  },
  methods: {
    onCancelBulkUpdateAppointmentsModal: function () {
      this.showBulkUpdateAppointmentsModal = false
    },
    bulkUpdatedAppointmentsActivity: function () {
      this.showBulkUpdateAppointmentsModal = false
      this.calendarAPI.refetchEvents()
    },
    clearFacilitiesMultiselect: function () {
      this.filters.facilities = []
      this.updateOptionsBasedOnFacilities()
    },
    clearUsersMultiselect: function () {
      this.filters.users = []
      this.updateOptionsBasedOnUsers()
    },
    clearActivitiesMultiselect: function () {
      this.filters.activities = []
      this.updateOptionsBasedOnActivities()
    },
    updateOptions: function (ignore, filteredOptions) {
      // ignore will be used to check if the updateMethod calling this method will not update it's own options, for example
      // if we call updateOptions from the method binded to the facilities filter we should not filter the items to the uniq
      // or it will set the options to be the filtered item(s)
      if (ignore !== 'facilities') {
        this.facilitiesOptions = uniqBy(flatten(filteredOptions.map(item => {
          return item.entity.facilities
        })), 'id')
      }

      if (ignore !== 'users') {
        let optionsWithUser = filteredOptions.filter(opt => opt.user)
        this.usersOptions = uniqBy(flatten(optionsWithUser.map(item => {
          return item.user
        })), 'id')
      }

      if (ignore !== 'activities') {
        this.activitiesOptions = uniqBy(flatten(filteredOptions.map(item => {
          return item.activity
        })), 'id')
      }

      if (this.facilitiesOptions.length === 1 && ignore !== 'facilities') {
        this.filters.facilities.splice(0, this.filters.facilities.length, this.facilitiesOptions[0])
      }

      if (this.usersOptions.length === 1 && ignore !== 'users') {
        // Some appointments can have null user because no dispenser was assigned to it. We need to check that,
        // and in the case of existing it needs to be treated as if it were a user so we don't force the choice of one user.
        if (filteredOptions.find(item => item.user === null) === undefined) {
          this.filters.users.splice(0, this.filters.users.length, this.usersOptions[0])
        }
      }

      if (this.activitiesOptions.length === 1 && this.filters.activities.length === 0 && ignore !== 'activities') {
        this.filters.activities.splice(0, this.filters.activities.length, this.activitiesOptions[0])
      }

      if (this.filters.facilities.length === 0 && this.filters.users.length === 0) {
        this.filters.activities = []
      }
    },
    updateOptionsBasedOnFacilities: function (facilities) {
      let isCleared = !facilities || facilities.length === 0

      // When the multiselect is cleared it can be cleared by two ways: clear one option or clear every option. If
      // one option only is cleared the facilities variable will come with the array of options that are still present
      // or empty if none is present and when the button to clear all is pressed, facilities var will come as undefined
      // This way we need to have something to filter the data with in this two cases.
      if (isCleared) {
        facilities = this.completeFacilities
        // If the options were cleared and both other filters are empty then we set the facilities options to the full options
        if (this.filters.users.length === 0 && this.filters.activities.length === 0) {
          this.facilitiesOptions = facilities
        }
      }

      // Here we filter the original full data to return the data where facility_id is equal to the
      // facilities id's that are on the filter
      let filteredOptions = this.readEntityActivityUsers.filter(entityActivityUser => {
        return entityActivityUser.entity.facilities.some(facility => {
          return facilities.some(_facility => _facility.id === facility.id)
        })
      })

      this.updateOptions('facilities', filteredOptions)
    },
    updateOptionsBasedOnUsers: function (users) {
      let isCleared = !users || users.length === 0
      let filteredOptions
      if (isCleared) {
        users = this.completeUsers
        filteredOptions = this.readEntityActivityUsers
        // If the users were cleared, then we may want to list the items without a dispenser also, so we need the full data
        if (this.filters.facilities.length === 0 && this.filters.activities.length === 0) {
          this.usersOptions = users
        }
      } else {
        // If there are users to filter we need to set the data to get only the activities with user and with user id equals to the filters
        filteredOptions = this.readEntityActivityUsers.filter(entityActivityUser => {
          return entityActivityUser.user && users.some(_user => _user.id === entityActivityUser.user.id)
        })
      }
      this.updateOptions('users', filteredOptions)
    },
    updateOptionsBasedOnActivities: function (activities) {
      let isCleared = !activities || activities.length === 0
      if (isCleared) {
        activities = this.completeActivities
        if (this.filters.facilities.length === 0 && this.filters.users.length === 0) {
          this.activitiesOptions = activities
        }
      }

      let filteredOptions = this.readEntityActivityUsers.filter(entityActivityUser => {
        return activities.some(_activity => _activity.id === entityActivityUser.activity.id)
      })

      this.updateOptionsBasedOnFacilities(this.filters.facilities)
      this.updateOptionsBasedOnUsers(this.filters.users)

      if (this.facilitiesOptions.length === 1) {
        this.filters.facilities.splice(0, this.filters.facilities.length, this.facilitiesOptions[0])
      }
      if (this.usersOptions.length === 1) {
        // Some appointments can have null user because no dispenser was assigned to it. We need to check that
        if (filteredOptions.find(item => item.user === null) === undefined) {
          this.filters.users.splice(0, this.filters.users.length, this.usersOptions[0])
        }
      }
    },
    createDispenserNameLabel: function (user) {
      if (user.nickname) {
        return `${user.name} (${user.nickname})`
      }
      return user.name
    },
    canCreateForDefaults: function () {
      if (!this.writeEntityActivityUsers.length) {
        return false
      }

      let { start, end } = this.getDefaults()

      return this.hasEntityActivityUserForPeriod(moment(start), moment(end))
    },
    loadLocalStorage: function () {
      return new Promise(resolve => {
        let state = LocalStorageHelper.get(FILTERS_CALENDAR)

        let filters = {
          facilities: [],
          activities: [],
          users: []
        }

        if (state !== null) {
          if (state.hasOwnProperty('facilities') && state.facilities !== null && state.facilities !== '') {
            let facilitiesIds = state.facilities.split(',').map(id => parseInt(id))
            filters.facilities = this.facilitiesOptions.filter(facility => facilitiesIds.includes(facility.id))
          }

          if (state.hasOwnProperty('activities') && state.activities !== null && state.activities !== '') {
            let activitiesIds = state.activities.split(',').map(id => parseInt(id))
            filters.activities = this.activitiesOptions.filter(activity => activitiesIds.includes(activity.id))
          }

          if (state.hasOwnProperty('users') && state.users !== null && state.users !== '') {
            let userIds = state.users.split(',').map(id => parseInt(id))
            filters.users = this.usersOptions.filter(user => userIds.includes(user.id))
          }
        }

        this.filters = { ...this.filters, ...filters }
        if (this.facilitiesOptions.length === 1) {
          this.filters.facilities.splice(0, this.filters.facilities.length, this.facilitiesOptions[0])
        }
        if (this.usersOptions.length === 1) {
          this.filters.users.splice(0, this.filters.users.length, this.usersOptions[0])
        }
        if (this.activitiesOptions.length === 1) {
          this.filters.activities.splice(0, this.filters.activities.length, this.activitiesOptions[0])
        }

        // Populate filters options based on localStorage
        if (filters.facilities !== '') {
          this.updateOptionsBasedOnFacilities(filters.facilities)
        }
        if (filters.users !== '') {
          this.updateOptionsBasedOnUsers(filters.users)
        }
        if (filters.activities !== '') {
          this.updateOptionsBasedOnActivities(filters.activities)
        }

        this.hasLoadedLocalStorage = true
        resolve()
      })
    },
    getDefaults: function () {
      if (!this.calendarAPI) {
        return
      }
      let activeStart = moment(this.getNextViewStart())
      let start = moment(`${activeStart.format('YYYY-MM-DD')} ${this.calendarOptions.slotMinTime}`)
      let end = start.clone().add(FORM_DEFAULTS.START_INCREMENT, FORM_DEFAULTS.INCREMENT_INTERVAL)
      if (!this.hasEntityActivityUserForPeriod(start, end)) {
        start = this.getFirstAvailableDay()
        end = start.clone().add(FORM_DEFAULTS.START_INCREMENT, FORM_DEFAULTS.INCREMENT_INTERVAL)
      }

      return {
        start: start.format('YYYY-MM-DD HH:mm:ss'),
        end: end.format('YYYY-MM-DD HH:mm:ss'),
        allDay: true,
        fromDefault: true
      }
    },
    persistFilters: function () {
      let filters = {
        facilities: this.filters.facilities.map(facility => facility.id).join(','),
        activities: this.filters.activities.map(activity => activity.id).join(','),
        users: this.filters.users.map(user => user.id).join(',')
      }
      LocalStorageHelper.set(FILTERS_CALENDAR, filters)
    },
    getNextViewStart: function () {
      let start = moment(this.calendarAPI.view.currentStart)
      let end = moment(this.calendarAPI.view.currentEnd)
      let now = moment()
      if (start.isSameOrBefore(now) && end.isSameOrAfter(now)) {
        return now.toISOString()
      }
      return start.toISOString()
    },
    syncSPICE: function () {
      let data = {
        start: moment(this.calendarAPI.view.activeStart).format('YYYY-MM-DD HH:mm:ss'),
        end: moment(this.calendarAPI.view.activeEnd).set({
          hour: 23,
          minute: 59,
          seconds: 59
        }).format('YYYY-MM-DD HH:mm:ss')
      }
      AppointmentService.spice(this.component.service.url, data)
        .then(() => {
          this.showToast(this.$t('clinical.appointments.spice_success'), this.$store.state.successToastOptions)
        }).catch(errors => {
          console.log(errors)
        })
    },
    appointmentUpdated: function ({ appointment }) {
      this.propsAppointmentForm.appointment = appointment
      this.loadEvents()
    },
    createWithDefaults: function () {
      let params = this.getDefaults()
      this.eventCreated(params)
    },
    dayClicked: function (date) {
      this.calendarAPI.gotoDate(moment(date).toISOString())
      this.calendarAPI.changeView('timeGridDay')
    },
    loadEntityActivityUsersRead: function () {
      return new Promise((resolve, reject) => {
        if (this.cancelationReadCallback !== null) {
          this.cancelationReadCallback()
        }

        let params = {
          ...this.getDefaultEntityActivityUsersParams(),
          accessType: accessTypeRead
        }

        ApiService.cancelableQuery(`${this.$store.state.evollauth_server}/entities-activities-users`, params, new axios.CancelToken(c => {
          this.cancelationReadCallback = c
        }))
          .then(response => {
            this.readEntityActivityUsers = response.data.data
            // Sets the filters options with all the information and makes a copy of the original full data so that later it doesn't need to be recalculated
            // facilities
            this.facilitiesOptions = uniqBy(flatten(this.readEntityActivityUsers.map(entityActivityUser => {
              return entityActivityUser.entity.facilities
            })), 'id')
            this.completeFacilities = this.facilitiesOptions

            /*for (let item of this.readEntityActivityUsers) {
              console.log(item.user)
            }*/

            // users
            this.usersOptions = uniqBy(this.readEntityActivityUsers.filter(entityActivityUser => entityActivityUser.user).map(entityActivityUser => entityActivityUser.user), 'id')
            this.completeUsers = this.usersOptions

            // activitiesOptions
            this.activitiesOptions = uniqBy(this.readEntityActivityUsers.map(activityUser => activityUser.activity), 'id')
            this.completeActivities = this.activitiesOptions
            resolve()
          })
          .catch(errors => {
            if (!axios.isCancel(errors)) {
              reject(errors)
            }
          })
          .finally(() => {
            this.cancelationReadCallback = null
          })
      })
    },
    loadEntityActivityUsersWrite: function () {
      if (this.cancelationWriteCallback !== null) {
        this.cancelationWriteCallback()
      }

      let params = {
        ...this.getDefaultEntityActivityUsersParams(),
        accessType: accessTypeWrite
      }

      ApiService.cancelableQuery(`${this.$store.state.evollauth_server}/entities-activities-users`, params, new axios.CancelToken(c => {
        this.cancelationWriteCallback = c
      }))
        .then(response => {
          this.writeEntityActivityUsers = response.data.data.filter(eau => eau.status_id === EntityActivityUserActiveStatus)
          this.inactiveEntityActivityUsers = response.data.data.filter(eau => eau.status_id === EntityActivityUserInactiveStatus)
        })
        .catch(errors => {
          if (!axios.isCancel(errors)) {
            console.log(errors)
          }
        })
        .finally(() => {
          this.cancelationWriteCallback = null
        })
    },
    getDefaultEntityActivityUsersParams: function () {
      return {
        infoType: AppointmentInfoType,
        serviceId: this.component.service.id,
        statusesOfApproval: '1,3',
        activities: `${DispenserFieldId},${DispenserPremises}`,
        start: moment(this.calendarAPI.view.activeStart).format('YYYY-MM-DD HH:mm:ss'),
        end: moment(this.calendarAPI.view.activeEnd).set({
          hour: 23,
          minute: 59,
          seconds: 59,
          millisecond: 0
        }).format('YYYY-MM-DD HH:mm:ss')
      }
    },
    datesDidUpdate: function () {
      this.$nextTick(() => {
        this.loadEntityActivityUsersRead()
          .then(() => {
            return this.loadLocalStorage()
          })
        this.loadEntityActivityUsersWrite()
        LocalStorageHelper.set(FILTERS_CALENDAR_VIEW, this.calendarAPI.view.type)
        LocalStorageHelper.set(FILTERS_CALENDAR_DAY, this.moment(this.calendarAPI.view.currentStart).format('YYYY-MM-DD'), false)
      })
    },
    updateCalendar: function () {
      this.showAppointmentForm = false
      this.showAppointmentInfo = false
      this.propsAppointmentForm = {}
      this.loadEvents()
    },
    modalClosed: function () {
      this.showAppointmentInfo = false
      this.propsAppointmentForm = {}
      this.showAppointmentForm = false
    },
    checkIfAppointmentIsOngoing: function (appointment) {
      return appointment !== null && appointment !== undefined && appointment.started_at !== null && (appointment.ended_at === null || (appointment.ended_at !== null && this.moment(appointment.ended_at).isAfter()))
    },
    updateEventFromUI: function ({ event, revert }) {
      try {
        if (!this.hasPermissionToEditEvent(event)) {
          throw new Error('Missing permissions')
        }

        const eventId = parseInt(event.id)
        const appointment = this.appointments.find(appointment => appointment.id === eventId)

        if (appointment === undefined) {
          throw new Error('Could not find an apointment with the given id')
        }

        if (this.checkIfAppointmentIsOngoing(appointment)) {
          this.showToast(this.$t('clinical.appointments.errors.start_date_or_end_date_cannot_be_updated_while_appointment_is_ongoing'), this.$store.state.errorToastOptions)
          throw new Error('Cannot update ongoing appointment')
        }

        const params = {
          start: this.$options.filters.utcTime(event.start),
          end: this.$options.filters.utcTime(event.end)
        }

        AppointmentService.update(this.component.service.url, event.id, params)
          .then(response => {
            this.showToast(this.$t('clinical.appointments.actions.updated'), this.$store.state.successToastOptions)
            if (response.data.error) {
              setTimeout(() => {
                this.showToast(this.$t('clinical.appointments.errors.spice_sync'), this.$store.state.errorToastOptions)
              }, this.$store.state.successToastOptions.duration)
            }
          })
          .catch(errors => {
            console.log(errors)
            let errorMessage = this.$t('clinical.appointments.something_went_wrong')
            if (errors.hasOwnProperty('response') && errors.response.status) {
              const status = errors.response.status
              if (status === 403) {
                errorMessage = this.$t('clinical.appointments.missing_permissions')
              } else if (status === 422) {
                const errorBag = new Errors()
                errorBag.record(errors.response.data.errors, 'clinical.appointments.errors.')
                errorMessage = this.$t(errorBag.first())
              }
            }
            this.showToast(errorMessage, this.$store.state.errorToastOptions)
            revert()
          })
      } catch (errors) {
        console.log(errors)
        revert()
      }
    },
    eventCreated: function ({ start, end, allDay, fromDefault = false }) {
      let facilities = this.filters.facilities.length ? this.facilitiesAdmin.filter(facility => this.filters.facilities.some(filterFacility => facility.id === filterFacility.id)) : this.facilitiesAdmin
      if (!facilities.length) {
        this.calendarAPI.unselect()
        return
      }
      this.propsAppointmentForm = {
        facilities
      }

      if (start && end) {
        start = moment(start)
        end = moment(end)
        if (allDay) {
          // If its same day get round up the next start if not select the calendar slotMinTime plus START_INCREMENT
          if (start.isSame(new Date(), 'day') && moment().isAfter(start)) {
            start = moment(this.getNextViewStart()).add(FORM_DEFAULTS.NEXT_START_INCREMENT, FORM_DEFAULTS.NEXT_START_INTERVAL).startOf(FORM_DEFAULTS.NEXT_START_INTERVAL)
            end = start.clone().add(FORM_DEFAULTS.START_INCREMENT, FORM_DEFAULTS.INCREMENT_INTERVAL)
            // If its not creating from the plus button
          } else if (!fromDefault) {
            start = moment(`${start.format('YYYY-MM-DD')} ${this.calendarOptions.slotMinTime}`)
            end = start.clone().add(FORM_DEFAULTS.START_INCREMENT, FORM_DEFAULTS.INCREMENT_INTERVAL)
            // If does not have entityActivityUser for 8h-8h45m try to get the next start
            if (!this.hasEntityActivityUserForPeriod(start, end)) {
              let nextStart = this.getFirstAvailableDay()
              // If the next available next start is equal to the day that the user clicked in the calendar assume those start/end
              if (nextStart.isSame(start, 'day')) {
                start = nextStart
                end = start.clone().add(FORM_DEFAULTS.START_INCREMENT, FORM_DEFAULTS.INCREMENT_INTERVAL)
              }
            }
          }
        }
        if (!this.hasEntityActivityUserForPeriod(start, end)) {
          this.calendarAPI.unselect()
          return
        }
        this.propsAppointmentForm.fromEvent = {
          start: start.format('YYYY-MM-DD HH:mm:ss'),
          end: end.format('YYYY-MM-DD HH:mm:ss')
        }
      }
      this.showAppointmentForm = true
    },
    hasEntityActivityUserForPeriod: function (start, end) {
      return this.writeEntityActivityUsers.some(entityActivityUser => {
        let startDate = moment(entityActivityUser.start_date)
        let endDate = moment(entityActivityUser.end_date)
        return ((!startDate.isValid() || startDate.isSameOrBefore(start)) && (!endDate.isValid() || endDate.isSameOrAfter(end)))
      })
    },
    eventSelected: function ({ event }) {
      let facilities = this.filters.facilities.length ? this.filters.facilities.filter(facility => this.facilitiesAdmin.some(_facility => facility.id === _facility.id)) : this.facilitiesAdmin
      this.propsAppointmentForm = {
        appointment: {
          id: event.id,
          ...event.extendedProps,
          source: null,
          start: moment(event.start).format('YYYY-MM-DD HH:mm:ss'),
          end: moment(event.end).format('YYYY-MM-DD HH:mm:ss')
        },
        writeEntityActivityUsers: this.writeEntityActivityUsers,
        facilities
      }
      this.showAppointmentInfo = true
    },
    loadEvents: function () {
      this.calendarAPI.refetchEvents()
    },
    hasPermissionToEditEvent: function (event) {
      return this.facilitiesAdmin.some(facility => facility.id === event.extendedProps.facility.id)
    },
    loadActivities: function () {
      this.$http({
        url: this.$store.state.evollauth_server + '/activities'
      }).then((response) => {
        this.activities = response.data.data
      }).catch((error) => {
        console.log(error)
      })
    },
    getFirstAvailableDay: function () {
      return this.writeEntityActivityUsers.map(entityActivityUser => {
        return moment(entityActivityUser.start_date)
      }).reduce(function (initialDate, startDate) {
        return startDate.isSameOrBefore(initialDate) ? startDate : initialDate
      })
    },
    onEntityActivityUserCreated: function () {
      this.showNewPendingActivity = false
      setTimeout(() => {
        this.newPendingActivityCreated = true
      }, 250)
    },
    onCloseEditUserActivityDate: function () {
      this.showNewPendingActivity = false
    }
  },
  computed: {
    ...mapGetters([
      'sidebarMinimized',
      'services'
    ]),
    ...mapGetters({
      userIsDispenser: 'auth/userIsDispenser'
    }),
    lowestAppointmentStart: function () {
      if (this.appointments.length > 0) {
        const appointmentsSortedByStart = [...this.appointments].sort((a, b) => moment(a.start).diff(b.start))
        return moment(appointmentsSortedByStart[0].start)
      } else {
        return null
      }
    },
    highestAppointmentEnd: function () {
      if (this.appointments.length > 0) {
        const appointmentsSortedByEnd = [...this.appointments].sort((a, b) => moment(a.end).diff(b.end))
        return moment(appointmentsSortedByEnd[appointmentsSortedByEnd.length - 1].end)
      } else {
        return null
      }
    },
    canBulkUpdateAppointments: function () {
      if (this.appointments && this.appointments.length > 0 && (this.filters.users.length > 0 || this.filters.facilities.length > 0)) {
        return this.component.service.user_permissions.filter(perm => this.filters.facilities.filter(fac => fac.entity_id === perm.entity_id)).some(permission => permission.access_type > 2)
      }
      return false
    },
    canSyncSPICE: function () {
      return !this.$store.getters.inProductionEnvironment
    },
    authService: function () {
      return this.services.find(service => service.url === this.$store.state.evollauth_server)
    },
    appointmentPermissions: function () {
      return this.component.service.user_permissions.filter(permission => permission.info_type === AppointmentInfoType)
    },
    canCreateAppointment: function () {
      return this.facilitiesAdmin.length > 0// && this.filters.facilities.length > 0
    },
    appointmentDetailsPermission: function () {
      return this.component.service.user_permissions.filter(permission => permission.info_type === AppointmentDetailsInfoType)
    },
    entities: function () {
      return this.component.service.entities.filter(entity => this.appointmentPermissions.some(permission => permission.entity_id === entity.id || permission.entity_id === null))
    },
    entitiesWrite: function () {
      return this.component.service.entities.filter(entity => this.appointmentPermissions.some(permission => (permission.entity_id === entity.id || permission.entity_id === null) && permission.access_type >= accessTypeWrite))
    },
    facilities: function () {
      return uniqBy(flatten(this.readEntityActivityUsers.map(entityActivityUser => {
        return entityActivityUser.entity.facilities
      })), 'id')
    },
    facilitiesAdmin: function () {
      return uniqBy(flatten(this.writeEntityActivityUsers.map(entityActivityUser => {
        return entityActivityUser.entity.facilities
      })), 'id')
    },
    /*
    facilitiesOptions: function () {
      let entityActivityUsers = this.readEntityActivityUsers

      /*if (this.filters.activities.length) {
        entityActivityUsers = entityActivityUsers.filter(entityActivityUser => {
          return this.filters.activities.some(activity => activity.id === entityActivityUser.activity.id)
        })
      }

      if (this.filters.users.length) {
        entityActivityUsers = entityActivityUsers.filter(entityActivityUser => {
          return entityActivityUser.user && this.filters.users.some(user => user.id === entityActivityUser.user.id)
        })
      }

      return uniqBy(flatten(entityActivityUsers.map(entityActivityUser => {
        return entityActivityUser.entity.facilities
      })), 'id')
    },
    activitiesOptions: function () {
      let options = []

      if (this.filters.facilities.length || this.filters.users.length) {
        let entityActivityUsers = this.readEntityActivityUsers
        if (this.filters.facilities.length) {
          entityActivityUsers = entityActivityUsers.filter(entityActivityUser => {
            return entityActivityUser.entity.facilities.some(facility => this.filters.facilities.some(_facility => _facility.id === facility.id))
          })
        }

        if (this.filters.users.length) {
          entityActivityUsers = entityActivityUsers.filter(entityActivityUser => {
            return entityActivityUser.user && this.filters.users.some(user => user.id === entityActivityUser.user.id)
          })
        }
        options = uniqBy(entityActivityUsers.map(activityUser => activityUser.activity), 'id')
      }

      return options
      let entityActivityUsers = this.readEntityActivityUsers
      return uniqBy(entityActivityUsers.map(activityUser => activityUser.activity), 'id')
    },
    usersOptions: function () {
      let entityActivityUsers = this.readEntityActivityUsers
      if (this.filters.facilities.length) {
        entityActivityUsers = entityActivityUsers.filter(entityActivityUser => {
          return entityActivityUser.entity.facilities.some(facility => {
            return this.filters.facilities.some(_facility => _facility.id === facility.id)
          })
        })
      }
      if (this.filters.activities.length) {
        entityActivityUsers = entityActivityUsers.filter(entityActivityUser => {
          return this.filters.activities.some(activity => activity.id === entityActivityUser.activity.id)
        })
      }

      let users = uniqBy(entityActivityUsers.filter(entityActivityUser => entityActivityUser.user).map(entityActivityUser => entityActivityUser.user), 'id')
      return users
    },
    */
    selectedLanguage: function () {
      return this.$i18n.locale()
    },
    pendingActivitiesWritePermissions: function () {
      let permissions = []
      if (this.authService) {
        permissions = this.authService.user_permissions.filter(permission => permission.info_type === PendingActivitiesInfoType && permission.access_type === accessTypeWrite)
      }
      return permissions
    },
  },
  watch: {
    facilities: function (facilities) {
      this.calendarAPI.setOption('selectable', facilities.length > 0)
    },
    'filters': {
      handler: function () {
        this.persistFilters()
        this.loadEvents()
      },
      deep: true
    },
    /*
    facilitiesOptions: function (newOptions, oldOptions) {
        console.log(newOptions, oldOptions)
      if (newOptions.length === 1 && this.filters.facilities.length === 0) {
        this.filters.facilities.splice(0, this.filters.facilities.length, newOptions[0])
      } /*else if (newOptions.length !== oldOptions.length) {
        this.filters.facilities = this.filters.facilities.filter(f => newOptions.some(facility => facility.id === f.id))
      }
    },
    usersOptions: function (newOptions, oldOptions) {
      if(newOptions.length === 1 && this.filters.users.length === 0){
        this.filters.users.splice(0, this.filters.users.length, newOptions[0])
      } else if (newOptions.length !== oldOptions.length) {
        this.filters.users = this.filters.users.filter(u => newOptions.some(user => u.id === user.id))
      }
    },
    activitiesOptions: function (newOptions, oldOptions) {
      if(newOptions.length === 1 && this.filters.activities.length === 0) {
        this.filters.activities.splice(0, this.filters.activities.length, newOptions[0])
      } else if (newOptions.length !== oldOptions.length) {
        this.filters.activities = this.filters.activities.filter(a => newOptions.some(activity => a.id === activity.id))
      }
    },
    */
    selectedLanguage: function (language) {
      if (this.calendarAPI) {
        this.calendarAPI.setOption('locale', language === 'gb' ? 'en' : language)
      }
    },
    sidebarMinimized: function () {
      if (this.calendarAPI) {
        this.calendarKey = this.calendarKey + 1
      }
    }
  }
}
</script>
<style lang='scss'>
  // you must include each plugins' css
  // paths prefixed with ~ signify node_modules
  // @import '~@fullcalendar/core/main.css';
  // @import '~@fullcalendar/daygrid/main.css';
  // @import '~@fullcalendar/timegrid/main.css';

  @mixin for-desktop {
    @media (min-width: 599px) {
      @content;
    }
  }

  #appointments-page {
    #btn-bulk-update-appointments {
      background-color: #f2f2f2 !important;

      i {
        color: #5b5b5b !important;
        margin-left: 2px;
      }
    }

    #btn-add-activity {
      background-color: #f2f2f2 !important;

      i {
        color: #5b5b5b !important;
        margin-left: 2px;
      }
    }

    .calendar-wrapper {
      position: relative;

      .vld-overlay {
        position: absolute;
        width: 100%;
        height: 100%;
        z-index: 500;

        .vld-background {
          position: absolute;
          width: 100%;
          height: 100%;
          background: rgb(245, 245, 245);
          opacity: 0.3;
        }

        .vld-icon {
          width: 100%;
          height: 100%;
          display: flex;
          justify-content: center;
          align-items: center;
        }
      }
    }

    .fc-header-toolbar {
      button {
        background-color: transparent;
        -webkit-box-shadow: 0 0 4px 0 rgba(98, 106, 119, 0.25);
        box-shadow: 0 0 4px 0 rgba(98, 106, 119, 0.25);
        border-radius: 25px;
        border: none;
        color: #d4d4d4;
        padding-top: 0.3em;
        padding-bottom: 0.3em;
        padding-left: 1em;
        padding-right: 1em;

        &:hover {
          color: #6e6e6e;
        }

        &:not(:first-child) {
          margin-left: 1em;
        }

        // &:disabled {
        //   color:#d4d4d4;
        // }
        &.fc-button-active {
          color: #6e6e6e;
          font-weight: 600;
        }

        &.fc-prev-button {
          margin-left: 1.5em;
        }

        &.fc-next-button,
        &.fc-prev-button {
          padding: 0.3em;
          color: #6e6e6e;

          span {
            font-weight: bold;
            font-size: 1.3em;
          }
        }
      }

      .fc-toolbar-title {
        color: #414141;
        font-weight: 600;
        font-size: 1.5em;
      }
    }

    .fc-daygrid-event, .fc-timegrid-event {
      background-color: #e8e8e8 !important;
      border-left-width: 10px;
      border-right-width: 0px;
      border-top-width: 0px;
      border-bottom-width: 0px;
      border-radius: 0%;

      .fc-event-main {
        color: #6E6E6E !important;
        padding-left: 5px;
        overflow: hidden;
      }
    }

    .person-name {
      color: #414141 !important;
      font-weight: 600;
    }

    .fc-timegrid-slot-label {
      color: #414141;
    }

    td.fc-timegrid-slot {
      border: 0.5px solid #d3d3d3 !important;
    }

    .fc-timegrid-cols td,
    .fc-timegrid-slot.fc-timegrid-slot-label,
    .fc-theme-standard .fc-scrollgrid,
    .fc-direction-ltr .fc-timeGridWeek-view .fc-scrollgrid tr > *:first-child,
    .fc-direction-ltr .fc-timeGridDay-view .fc-scrollgrid tr > *:first-child {
      border: none !important;
    }

    .fc-timeGridWeek-view td.fc-timegrid-slot.fc-timegrid-slot-lane,
    .fc-timeGridDay-view td.fc-timegrid-slot.fc-timegrid-slot-lane {
      // border-top: solid !important;
      border-right: none !important;
      border-left: none !important;
    }

    .fc-timeGridWeek-view tr:first-of-type td.fc-timegrid-slot.fc-timegrid-slot-lane,
    .fc-timeGridDay-view tr:first-of-type td.fc-timegrid-slot.fc-timegrid-slot-lane {
      border-top: 0.5px solid #d3d3d3 !important;
    }

    .fc-scrollgrid-section.fc-scrollgrid-section-header.fc-scrollgrid-section-sticky > td {
      border: none;
    }

    .fc-dayGridMonth-view .fc-scrollgrid-section.fc-scrollgrid-section-body > td {
      border-left: 0.5px solid #d3d3d3 !important;
      border-right: 0.5px solid #d3d3d3 !important;
      border-bottom: 0.5px solid #d3d3d3 !important;
    }

    .fc-scrollgrid-section.fc-scrollgrid-section-header.fc-scrollgrid-section-sticky > td {
      border-bottom: 0.5px solid #d3d3d3 !important;
    }

    .fc-scrollgrid-section-sticky > td {
      background-color: transparent;
    }

    th.fc-day-today, td.fc-day-today {
      background-color: #ebebeb;
    }

    .fc-day-grid-event > .fc-content {
      white-space: normal;
      text-overflow: ellipsis;
      max-height: 20px;
    }

    .fc-day-grid-event > .fc-content:hover {
      max-height: none !important;
    }

    .calendar__allday__label {
      font-size: 0.9rem;
    }

    .fc-header-left, fc-header-center, fc-header-right {
      width: 100%;
      display: block;
    }

    .floating__button {
      position: fixed;
      z-index: 9999;
      color: #29c4a9;
      font-size: 2rem;
    }

    .fc-col-header-cell {
      color: #d3d3d3;

      &.fc-day-today {
        color: #6E6E6E;
      }
    }

    .calendar__header__label {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;

      & .calendar__header__label__weekday {
        font-size: 0.6rem;
        text-transform: uppercase;
        @include for-desktop {
          font-size: 1.1rem;
        }
      }

      & .calendar__header__label__day {
        font-size: 0.8rem;
        font-weight: bold;
        @include for-desktop {
          font-size: 2.1rem;
        }

        &.view__dayGridMonth {
          display: none !important;
        }
      }
    }

    // .fc td, .fc th, .fc-timegrid, .fc-timeGridWeek-view, .fc-view {
    //   border-style: double !important;
    //   border-width: 0px !important;
    //   border-color: #aaa !important;
    // }

    .fc-col-header-cell {
      border-style: double !important;
      border-width: 0px !important;
      border-color: #aaa !important;
    }

    .fc-scrollgrid-section-header {
      border-left-style: double !important;
      border-left-width: 0px !important;
      border-left-color: #aaa !important;
    }

    .fc-timegrid-slot {
      height: 50px !important;
    }

    .fc-button-primary {
      color: #fff;
      background-color: #29c4a9;
      border-color: #29c4a9;
    }

    .fc-content {
      white-space: initial !important;
    }
  }

  #appointments-page {
    & span.fa.fa-upload {
      font-size: 1.5rem;
      margin-right: 5px;

      &:hover {
        cursor: pointer;
      }
    }
  }
</style>
